import { useState, useCallback, useRef } from 'react';

/**
 * Hook for caching API responses to reduce redundant calls
 */
export const useApiCache = (defaultTTL = 5 * 60 * 1000) => { // 5 minutes default
  const cache = useRef(new Map());

  const get = useCallback((key) => {
    const item = cache.current.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      cache.current.delete(key);
      return null;
    }
    
    return item.data;
  }, []);

  const set = useCallback((key, data, ttl = defaultTTL) => {
    cache.current.set(key, {
      data,
      expiry: Date.now() + ttl
    });
  }, [defaultTTL]);

  const clear = useCallback((key) => {
    if (key) {
      cache.current.delete(key);
    } else {
      cache.current.clear();
    }
  }, []);

  const has = useCallback((key) => {
    const item = cache.current.get(key);
    return item && Date.now() <= item.expiry;
  }, []);

  return { get, set, clear, has };
};

/**
 * Hook for debouncing API calls
 */
export const useDebounce = (callback, delay = 300) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

/**
 * Hook for throttling API calls
 */
export const useThrottle = (callback, delay = 1000) => {
  const lastRun = useRef(Date.now());

  return useCallback((...args) => {
    if (Date.now() - lastRun.current >= delay) {
      callback(...args);
      lastRun.current = Date.now();
    }
  }, [callback, delay]);
};

/**
 * Hook for managing request queues to avoid duplicate API calls
 */
export const useRequestQueue = () => {
  const pendingRequests = useRef(new Map());

  const enqueue = useCallback(async (key, requestFn) => {
    // If request is already pending, return the same promise
    if (pendingRequests.current.has(key)) {
      return pendingRequests.current.get(key);
    }

    // Create new request promise
    const promise = requestFn().finally(() => {
      pendingRequests.current.delete(key);
    });

    pendingRequests.current.set(key, promise);
    return promise;
  }, []);

  const isPending = useCallback((key) => {
    return pendingRequests.current.has(key);
  }, []);

  const clear = useCallback((key) => {
    if (key) {
      pendingRequests.current.delete(key);
    } else {
      pendingRequests.current.clear();
    }
  }, []);

  return { enqueue, isPending, clear };
};

/**
 * Combined hook for efficient API management
 */
export const useEfficientAPI = (options = {}) => {
  const { cacheTime = 5 * 60 * 1000, debounceTime = 300 } = options;
  
  const cache = useApiCache(cacheTime);
  const requestQueue = useRequestQueue();
  
  const cachedFetch = useCallback(async (key, requestFn, useCache = true) => {
    // Try cache first
    if (useCache && cache.has(key)) {
      return cache.get(key);
    }

    // Use request queue to prevent duplicate calls
    const data = await requestQueue.enqueue(key, requestFn);
    
    // Cache the result
    if (useCache && data) {
      cache.set(key, data);
    }
    
    return data;
  }, [cache, requestQueue]);

  const debouncedFetch = useDebounce(cachedFetch, debounceTime);
  
  return {
    cachedFetch,
    debouncedFetch,
    cache,
    requestQueue,
    clearCache: cache.clear,
    isPending: requestQueue.isPending
  };
};